/**
 * Chanak.co – Deferred Asset Loader
 * -----------------------------------------------------------
 * Purpose: Speed up First Contentful Paint / LCP by deferring
 * everything that is NOT needed for the very first paint:
 *   - Non-critical CSS files (reviews app, popups, upsell apps, etc.)
 *   - Non-critical JS files (chat widgets, tracking pixels, upsell apps)
 *   - Autoplay <video> tags (only start playback once in viewport)
 *   - Below-the-fold <img> (native lazy + a JS fallback)
 *   - Below-the-fold <iframe> (YouTube embeds, maps, etc.)
 *
 * HOW TO USE ON SHOPIFY:
 * 1. Upload this file via Online Store > Themes > Edit code > Assets > Add a new asset
 * 2. In theme.liquid, right before </body>, add:
 *      {{ 'deferred-loader.js' | asset_url | script_tag }}
 * 3. For any <script src="..."> or <link rel="stylesheet" href="..."> tag you want
 *    deferred (added by apps in theme.liquid/header), change:
 *       <script src="X.js"></script>            ->  <script data-defer-src="X.js"></script>
 *       <link rel="stylesheet" href="X.css">     ->  <link data-defer-href="X.css" rel="stylesheet" media="print" disabled>
 *    This script will swap them back to real src/href once the page has settled.
 * 4. For videos, make sure they have preload="none" and NOT autoplay in markup;
 *    this script starts them only once scrolled into view.
 *
 * Requires jQuery (Shopify themes almost always already load it; if not,
 * this script still works because the DOM-ready/observer logic below
 * is written in a jQuery-first style but falls back gracefully).
 * -----------------------------------------------------------
 */
(function ($) {
  "use strict";

  var $doc = $(document);

  /* ---------------------------------------------------------
   * 1. MASTER CONTROLLER
   *    Waits for window 'load' (i.e. all critical/first-paint
   *    assets already done) before doing ANY deferred work.
   * --------------------------------------------------------- */
  function initDeferredLoader() {
    deferStylesheets();
    deferScripts();
    initLazyVideos();
    initLazyImagesFallback();
    initLazyIframes();
  }

  // Run after full load, with a tiny buffer so we don't compete
  // with the browser's own paint/layout work right at load time.
  $(window).on("load", function () {
    setTimeout(initDeferredLoader, 150);
  });

  /* ---------------------------------------------------------
   * 2. DEFER NON-CRITICAL CSS
   *    Targets: <link data-defer-href="..." media="print" disabled>
   *    Swaps in the real stylesheet only after page load.
   * --------------------------------------------------------- */
  function deferStylesheets() {
    $("link[data-defer-href]").each(function () {
      var $link = $(this);
      var href = $link.attr("data-defer-href");
      if (!href) return;

      $link
        .attr("href", href)
        .attr("media", "all")
        .removeAttr("disabled")
        .removeAttr("data-defer-href");
    });
  }

  /* ---------------------------------------------------------
   * 3. DEFER NON-CRITICAL JS
   *    Targets: <script data-defer-src="..."></script>
   *    Injects them one by one after load so they don't fight
   *    for bandwidth/main-thread with critical rendering work.
   * --------------------------------------------------------- */
  function deferScripts() {
    var $scripts = $("script[data-defer-src]");
    var queue = [];

    $scripts.each(function () {
      var $s = $(this);
      var src = $s.attr("data-defer-src");
      if (src) queue.push({ src: src, $el: $s });
    });

    function loadNext() {
      if (!queue.length) return;
      var item = queue.shift();
      var el = document.createElement("script");
      el.src = item.src;
      el.async = true;
      el.onload = el.onerror = loadNext; // keep going regardless
      item.$el.replaceWith(el);
    }

    loadNext();
  }

  /* ---------------------------------------------------------
   * 4. LAZY VIDEOS
   *    Videos only start loading/playing once they scroll into
   *    view (with a margin so they're ready just before visible).
   *    Markup expected: <video data-lazy-video preload="none" ...>
   *      <source data-src="video.mp4" type="video/mp4">
   *    </video>
   * --------------------------------------------------------- */
  function initLazyVideos() {
    var videos = document.querySelectorAll("video[data-lazy-video]");
    if (!videos.length) return;

    if (!("IntersectionObserver" in window)) {
      // Fallback: just load them all (older browsers)
      videos.forEach(hydrateVideo);
      return;
    }

    var observer = new IntersectionObserver(
      function (entries, obs) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            hydrateVideo(entry.target);
            obs.unobserve(entry.target);
          }
        });
      },
      { rootMargin: "200px 0px" }
    );

    videos.forEach(function (v) {
      observer.observe(v);
    });
  }

  function hydrateVideo(videoEl) {
    var $video = $(videoEl);
    if ($video.data("hydrated")) return;
    $video.data("hydrated", true);

    $video.find("source[data-src]").each(function () {
      $(this).attr("src", $(this).attr("data-src"));
    });

    videoEl.load();
    var playPromise = videoEl.play();
    if (playPromise && playPromise.catch) {
      playPromise.catch(function () {
        /* autoplay blocked (mobile data saver etc.) — silently ignore */
      });
    }
  }

  /* ---------------------------------------------------------
   * 5. LAZY IMAGES (fallback for browsers/edge-cases)
   *    Most images should just use native loading="lazy".
   *    This is a safety net for any <img data-src="..."> markup
   *    (e.g. custom sliders that don't support native lazy).
   * --------------------------------------------------------- */
  function initLazyImagesFallback() {
    var imgs = document.querySelectorAll("img[data-src]");
    if (!imgs.length) return;

    if (!("IntersectionObserver" in window)) {
      imgs.forEach(function (img) {
        img.src = img.getAttribute("data-src");
      });
      return;
    }

    var observer = new IntersectionObserver(
      function (entries, obs) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            var img = entry.target;
            img.src = img.getAttribute("data-src");
            if (img.getAttribute("data-srcset")) {
              img.srcset = img.getAttribute("data-srcset");
            }
            img.removeAttribute("data-src");
            obs.unobserve(img);
          }
        });
      },
      { rootMargin: "300px 0px" }
    );

    imgs.forEach(function (img) {
      observer.observe(img);
    });
  }

  /* ---------------------------------------------------------
   * 6. LAZY IFRAMES (YouTube embeds, maps, etc.)
   *    Markup expected: <iframe data-src="..." ...></iframe>
   * --------------------------------------------------------- */
  function initLazyIframes() {
    var frames = document.querySelectorAll("iframe[data-src]");
    if (!frames.length) return;

    if (!("IntersectionObserver" in window)) {
      frames.forEach(function (f) {
        f.src = f.getAttribute("data-src");
      });
      return;
    }

    var observer = new IntersectionObserver(
      function (entries, obs) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            var f = entry.target;
            f.src = f.getAttribute("data-src");
            f.removeAttribute("data-src");
            obs.unobserve(f);
          }
        });
      },
      { rootMargin: "200px 0px" }
    );

    frames.forEach(function (f) {
      observer.observe(f);
    });
  }
})(jQuery);